import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

export default function Footer() {
  // Scroll to top when clicking links
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-gradient-to-b from-black via-gray-900 to-black text-white relative overflow-hidden">
      {/* Decorative gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5 pointer-events-none" />

      {/* ================= TOP SECTION WITH CTA ================= */}
      <div className="relative border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-white">
                Ready to Transform Your Business?
              </h2>
              <p className="text-white text-sm md:text-base">
                Let's discuss how we can help you achieve your goals.
              </p>
            </div>
            <Link
              to="/contact"
              onClick={handleLinkClick}
              className="group px-8 py-4 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-all duration-300 flex items-center gap-2 shadow-lg shadow-yellow-500/20"
            >
              Get Started
              <ArrowUpRight
                className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                size={18}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* ================= MAIN FOOTER CONTENT ================= */}
      <div className="relative max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">

          {/* ================= BRAND COLUMN ================= */}
          <div className="lg:col-span-4 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center shadow-lg p-1 overflow-hidden border border-yellow-500/20">
                <img
                  src="/assets/logo.png"
                  alt="Hawkins & Brooks Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3
                className="text-xl font-bold tracking-wide text-white"
                style={{ fontFamily: "'Lora', 'Georgia', serif" }}
              >
                Hawkins & Brooks
              </h3>
            </div>

            <p className="text-white text-sm leading-relaxed">
              Empowering businesses with strategic advisory, financial insights,
              and technology-driven solutions for sustainable growth and innovation.
            </p>
          </div>

          {/* ================= QUICK LINKS ================= */}
          <div className="lg:col-span-2">
            <h4 className="text-base font-bold mb-5 text-white flex items-center gap-2">
              <span className="w-1 h-5 bg-yellow-500 rounded-full"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Our Work", path: "/our-work" },
                { name: "Contact", path: "/contact" },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    onClick={handleLinkClick}
                    className="group text-sm text-white hover:text-yellow-400 transition-colors flex items-center gap-2"
                  >
                    <ArrowUpRight
                      size={14}
                      className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                    />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ================= SERVICES ================= */}
          <div className="lg:col-span-3">
            <h4 className="text-base font-bold mb-5 text-white flex items-center gap-2">
              <span className="w-1 h-5 bg-yellow-500 rounded-full"></span>
              Our Services
            </h4>
            <ul className="space-y-3">
              {[
                "Strategy Consulting",
                "Financial Advisory",
                "Technology Consulting",
                "Policy & Regulatory",
                "Risk & Compliance",
              ].map((service) => (
                <li
                  key={service}
                  className="text-sm text-white flex items-start gap-2"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 mt-1.5 flex-shrink-0" />
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* ================= CONTACT INFO ================= */}
          <div className="lg:col-span-3">
            <h4 className="text-base font-bold mb-5 text-white flex items-center gap-2">
              <span className="w-1 h-5 bg-yellow-500 rounded-full"></span>
              Get In Touch
            </h4>

            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm">
                <div className="w-9 h-9 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} className="text-yellow-400" />
                </div>
                <div className="text-white">
                 <p className="font-medium text-white mb-1">Corporate Office</p>
<p className="leading-relaxed">
  Hawkings &amp; Brooks Consultancy<br />
  C/O Blackthorn Chambers<br />
  Office Suite# 18, 18th Floor,<br />
  Gold Tower, Wave One Tower,<br />
  L-2A, Pocket G, Sector 18,<br />
  Noida, Uttar Pradesh 201301
</p>

                </div>
              </li>

              <li className="flex items-start gap-3 text-sm">
                <div className="w-9 h-9 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone size={16} className="text-yellow-400" />
                </div>
                <a
                  href="tel:+919217468906"
                  className="text-white hover:text-yellow-400 transition"
                >
                  +91 9217468906
                </a>
              </li>

              <li className="flex items-start gap-3 text-sm">
                <div className="w-9 h-9 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-yellow-400" />
                </div>
                <a
                  href="mailto:hawkinsandbrooks@gmail.com"
                  className="text-white hover:text-yellow-400 transition break-all"
                >
                  hawkinsandbrooks@gmail.com
                </a>
              </li>
            </ul>

            {/* Business Hours */}
            <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="text-xs font-semibold text-yellow-400 mb-2">
                Business Hours
              </p>
              <p className="text-xs text-white">
                Mon – Sat: 9:30 AM – 6:30 PM
              </p>
              <p className="text-xs text-white">Sunday: Closed</p>
            </div>
          </div>
        </div>
      </div>

      {/* ================= BOTTOM BAR ================= */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-white text-center md:text-left">
              © {new Date().getFullYear()}{" "}
              <span
                className="text-white font-semibold"
                style={{ fontFamily: "'Lora', 'Georgia', serif" }}
              >
                Hawkins & Brooks
              </span>
              . All rights reserved.
            </p>

            <div className="flex flex-wrap gap-6 justify-center">
              <Link
                to="/privacy-policy"
                onClick={handleLinkClick}
                className="text-white hover:text-yellow-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/contact"
                onClick={handleLinkClick}
                className="text-white hover:text-yellow-400 transition-colors"
              >
                Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
